import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Card,
  Flex,
  Input,
  Modal,
  Skeleton,
  Tag,
  Typography,
  message,
  theme,
} from '@/shared/antd-imports';
import { CheckOutlined, CloseOutlined, DiffOutlined } from '@ant-design/icons';
import { useDecideBudgetExtensionMutation, useGetProjectBudgetExtensionsQuery } from '@/store/api';
import type { ClientBudgetExtension, ClientExtensionReason, ClientExtensionStatus } from '@/types/budget-extensions';
import { formatCalendarDate, formatMinor } from '@/utils/money';

interface BudgetExtensionsCardProps {
  projectId: string;
}

type Decision = 'approve' | 'reject';

const MAX_COMMENT = 2000;

/**
 * Spec 16 §1: budget changes (extensions / change orders) the provider sent for approval. The client approves or
 * rejects each one with an optional comment; decided ones stay listed. Renders nothing when there are none.
 */
export const BudgetExtensionsCard: React.FC<BudgetExtensionsCardProps> = ({ projectId }) => {
  const { t } = useTranslation();
  const { data, isLoading, isError, isFetching, refetch } = useGetProjectBudgetExtensionsQuery(projectId);
  const [deciding, setDeciding] = useState<{ extension: ClientBudgetExtension; decision: Decision } | null>(null);

  const title = (
    <Flex gap={8} align="center">
      <DiffOutlined />
      {t('budgetExtensions.title', { defaultValue: 'Budget changes' })}
    </Flex>
  );

  if (isLoading) {
    return (
      <Card title={title} style={{ marginBottom: 16 }}>
        <Skeleton active paragraph={{ rows: 2 }} title={false} />
      </Card>
    );
  }
  if (isError) {
    return (
      <Card title={title} style={{ marginBottom: 16 }}>
        <Alert
          type="error"
          showIcon
          message={t('budgetExtensions.loadError', { defaultValue: 'The budget changes could not be loaded.' })}
          action={
            <Button size="small" onClick={() => refetch()} loading={isFetching}>
              {t('budgetExtensions.retry', { defaultValue: 'Try again' })}
            </Button>
          }
        />
      </Card>
    );
  }

  const body = data?.body;
  if (!body?.enabled || body.extensions.length === 0) return null;
  const waiting = body.extensions.filter(item => item.can_decide).length;

  return (
    <Card title={title} style={{ marginBottom: 16 }}>
      <Flex vertical gap={12}>
        {waiting > 0 && (
          <Alert
            type="info"
            showIcon
            message={t('budgetExtensions.waiting', {
              defaultValue: '{{count}} change(s) need your approval.',
              count: waiting,
            })}
          />
        )}
        {body.extensions.map(extension => (
          <ExtensionItem
            key={extension.id}
            extension={extension}
            onDecide={decision => setDeciding({ extension, decision })}
          />
        ))}
      </Flex>
      {deciding && (
        <DecisionModal
          projectId={projectId}
          extension={deciding.extension}
          decision={deciding.decision}
          onClose={() => setDeciding(null)}
        />
      )}
    </Card>
  );
};

const STATUS_COLOR: Record<ClientExtensionStatus, string> = {
  sent: 'processing',
  approved: 'success',
  rejected: 'error',
};

const useLabels = () => {
  const { t } = useTranslation();
  const status: Record<ClientExtensionStatus, string> = {
    sent: t('budgetExtensions.status.sent', { defaultValue: 'Waiting for you' }),
    approved: t('budgetExtensions.status.approved', { defaultValue: 'Approved' }),
    rejected: t('budgetExtensions.status.rejected', { defaultValue: 'Rejected' }),
  };
  const reason: Record<ClientExtensionReason, string> = {
    new_requirement: t('budgetExtensions.reason.new_requirement', { defaultValue: 'New requirement' }),
    client_change: t('budgetExtensions.reason.client_change', { defaultValue: 'Change you asked for' }),
    delay: t('budgetExtensions.reason.delay', { defaultValue: 'Delay' }),
    other: t('budgetExtensions.reason.other', { defaultValue: 'Other' }),
  };
  return { status, reason };
};

const amountText = (extension: ClientBudgetExtension, language: string, timeOnly: string): string => {
  if (extension.amount_minor === 0 || !extension.currency) return timeOnly;
  const sign = extension.amount_minor > 0 ? '+' : '−';
  return `${sign}${formatMinor(Math.abs(extension.amount_minor), extension.currency, language)}`;
};

const ExtensionItem: React.FC<{ extension: ClientBudgetExtension; onDecide: (decision: Decision) => void }> = ({
  extension,
  onDecide,
}) => {
  const { t, i18n } = useTranslation();
  const { token } = theme.useToken();
  const labels = useLabels();
  const meta = [
    labels.reason[extension.reason],
    extension.extra_days
      ? t('budgetExtensions.extraDays', { defaultValue: '+{{count}} day(s) to the deadline', count: extension.extra_days })
      : null,
    extension.decided_at
      ? t('budgetExtensions.decidedOn', {
          defaultValue: 'Decided on {{date}}',
          date: formatCalendarDate(extension.decided_at, i18n.language) ?? '',
        })
      : extension.sent_at
        ? t('budgetExtensions.sentOn', {
            defaultValue: 'Sent on {{date}}',
            date: formatCalendarDate(extension.sent_at, i18n.language) ?? '',
          })
        : null,
  ].filter(Boolean);

  return (
    <div style={{ border: `1px solid ${token.colorBorderSecondary}`, borderRadius: 8, padding: 12 }}>
      <Flex justify="space-between" align="flex-start" gap={12} wrap="wrap">
        <Flex vertical gap={4} style={{ minWidth: 0, flex: '3 1 240px' }}>
          <Flex gap={8} align="center" wrap="wrap">
            <Typography.Text strong>{extension.code}</Typography.Text>
            <Tag color={STATUS_COLOR[extension.status]} style={{ marginInlineEnd: 0 }}>
              {labels.status[extension.status]}
            </Tag>
          </Flex>
          <Typography.Paragraph style={{ marginBottom: 0, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
            {extension.description}
          </Typography.Paragraph>
          <Typography.Text type="secondary" style={{ fontSize: 13 }}>
            {meta.join(' · ')}
          </Typography.Text>
          {extension.client_comment && (
            <Typography.Text type="secondary" style={{ fontSize: 13 }}>
              {t('budgetExtensions.yourComment', { defaultValue: 'Your comment: "{{comment}}"', comment: extension.client_comment })}
            </Typography.Text>
          )}
        </Flex>
        <Flex vertical gap={8} align="flex-end" style={{ flex: '1 1 160px' }}>
          <Typography.Text strong style={{ fontVariantNumeric: 'tabular-nums' }}>
            {amountText(extension, i18n.language, t('budgetExtensions.timeOnly', { defaultValue: 'Time only' }))}
          </Typography.Text>
          {extension.can_decide && (
            <Flex gap={8} wrap="wrap" justify="flex-end">
              <Button icon={<CloseOutlined />} onClick={() => onDecide('reject')}>
                {t('budgetExtensions.reject', { defaultValue: 'Reject' })}
              </Button>
              <Button type="primary" icon={<CheckOutlined />} onClick={() => onDecide('approve')}>
                {t('budgetExtensions.approve', { defaultValue: 'Approve' })}
              </Button>
            </Flex>
          )}
        </Flex>
      </Flex>
    </div>
  );
};

const DecisionModal: React.FC<{
  projectId: string;
  extension: ClientBudgetExtension;
  decision: Decision;
  onClose: () => void;
}> = ({ projectId, extension, decision, onClose }) => {
  const { t, i18n } = useTranslation();
  const [decide, { isLoading }] = useDecideBudgetExtensionMutation();
  const [comment, setComment] = useState('');
  const isApprove = decision === 'approve';
  const amount = amountText(extension, i18n.language, t('budgetExtensions.timeOnly', { defaultValue: 'Time only' }));

  const handleOk = async () => {
    try {
      await decide({ projectId, extensionId: extension.id, decision, comment: comment.trim() || undefined }).unwrap();
      message.success(
        isApprove
          ? t('budgetExtensions.approvedToast', { defaultValue: 'Thank you. The change is approved.' })
          : t('budgetExtensions.rejectedToast', { defaultValue: 'The change is rejected. The team has been told.' })
      );
      onClose();
    } catch (error) {
      const data = (error as { data?: { message?: unknown } } | undefined)?.data;
      message.error(
        typeof data?.message === 'string'
          ? data.message
          : t('budgetExtensions.saveError', { defaultValue: 'Your decision could not be saved. Please try again.' })
      );
    }
  };

  return (
    <Modal
      open
      title={
        isApprove
          ? t('budgetExtensions.approveTitle', { defaultValue: 'Approve {{code}}?', code: extension.code })
          : t('budgetExtensions.rejectTitle', { defaultValue: 'Reject {{code}}?', code: extension.code })
      }
      okText={isApprove ? t('budgetExtensions.approve', { defaultValue: 'Approve' }) : t('budgetExtensions.reject', { defaultValue: 'Reject' })}
      okButtonProps={{ danger: !isApprove }}
      cancelText={t('budgetExtensions.cancel', { defaultValue: 'Cancel' })}
      confirmLoading={isLoading}
      onOk={() => void handleOk()}
      onCancel={onClose}
      destroyOnClose
    >
      <Flex vertical gap={12}>
        <Typography.Paragraph style={{ marginBottom: 0 }}>
          {isApprove
            ? t('budgetExtensions.approveBody', {
                defaultValue: 'You agree to this change of the project budget ({{amount}}) and timeline.',
                amount,
              })
            : t('budgetExtensions.rejectBody', { defaultValue: 'The project budget and timeline stay as they are.' })}
        </Typography.Paragraph>
        <Input.TextArea
          value={comment}
          onChange={event => setComment(event.target.value)}
          maxLength={MAX_COMMENT}
          autoSize={{ minRows: 2, maxRows: 5 }}
          placeholder={t('budgetExtensions.commentPlaceholder', { defaultValue: 'Comment for the team (optional)' })}
          aria-label={t('budgetExtensions.comment', { defaultValue: 'Comment' })}
        />
      </Flex>
    </Modal>
  );
};

export default BudgetExtensionsCard;
